import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarHeart, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { HardDates } from "@/components/forecast/HardDates";

const short = (d) => d.weekday.slice(0, 3);

const Plan = ({ day, onClear }) => (
  <div className="mm-soft p-4 mt-3" data-testid="forecast-plan">
    <div className="font-semibold text-sm mb-2">Your small plan for {day.weekday}</div>
    <ul className="space-y-1.5">{day.plan.map((p) => <li key={p.title} className="text-sm"><Link to={p.link || "/coping"} className="font-semibold text-mm-primary hover:underline">{p.title}</Link> <span className="text-mm-muted">· {p.detail}</span></li>)}</ul>
    <button onClick={onClear} className="text-xs text-mm-muted underline mt-2" data-testid="forecast-plan-clear">Clear plan</button>
  </div>
);

// Gentle 7-day mood "weather" from the member's own patterns
export const ForecastCard = () => {
  const [params] = useSearchParams();
  const [fc, setFc] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/forecast").then((r) => {
    setFc(r.data);
    const want = params.get("forecast_plan");
    setSel((s) => s ?? Math.max(0, r.data.days?.findIndex((d) => d.date === want) ?? 0));
  }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!fc) return null;
  if (!fc.ready) return (
    <div className="mm-card p-5 mb-6 flex items-center gap-4" data-testid="forecast-pending">
      <div className="text-3xl" aria-hidden>🌤️</div>
      <div className="text-sm"><div className="font-semibold">Your mood forecast is on its way</div><div className="text-mm-muted">It appears after 3 weeks of check-ins{fc.days_needed ? ` (${fc.days_needed} more days)` : ""} on at least {fc.min_checkin_days} different days. So far: {fc.checkin_days}.</div></div>
    </div>
  );
  const day = fc.days[sel ?? 0];
  const heavy = day.weather === "cloudy" || day.weather === "stormy";
  const plan = async () => { setBusy(true); try { await api.post("/forecast/plan", { date: day.date }); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const clear = async () => { await api.delete(`/forecast/plan/${day.date}`); load(); };
  const toggle = async (v) => { try { await api.put("/forecast/settings", { heads_up: v }); setFc({ ...fc, heads_up: v }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="mm-card p-5 mb-6 mm-fade-up" data-testid="forecast-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-bold text-base md:text-lg">Your mood weather</h2>
        <span className="text-xs text-mm-muted">From your own patterns · might, not will</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {fc.days.map((d, k) => (
          <button key={d.date} onClick={() => setSel(k)} className={`rounded-2xl py-2 flex flex-col items-center gap-0.5 border transition-colors ${k === sel ? "border-mm-primary bg-mm-primary/10" : "border-transparent hover:bg-mm-primary/5"}`} aria-label={`${d.weekday}: ${d.label}`} data-testid={`forecast-day-${k}`}>
            <span className="text-[11px] font-semibold text-mm-muted">{k === 0 ? "Today" : short(d)}</span>
            <span className="text-2xl" aria-hidden>{d.icon}</span>
            {d.plan && <span className="w-1.5 h-1.5 rounded-full bg-mm-primary" aria-hidden />}
          </button>
        ))}
      </div>
      <div className="mt-3 text-sm" data-testid="forecast-detail"><b>{day.weekday}: {day.label}.</b> <span className="text-mm-muted">{day.reason}{heavy ? ". It might feel a bit heavier, and that's okay." : "."}</span></div>
      {day.plan ? <Plan day={day} onClear={clear} /> : heavy && (
        <button onClick={plan} disabled={busy} className="mm-btn mt-3 !py-2 text-sm" data-testid="forecast-make-plan">{busy && <Loader2 size={14} className="animate-spin" />}Plan something small that helps</button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-mm-line text-sm">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={fc.heads_up} onChange={(e) => toggle(e.target.checked)} className="w-4 h-4 accent-[var(--mm-primary)]" data-testid="forecast-headsup-toggle" />
          Evening heads-up before a heavier day
        </label>
        <HardDates dates={fc.hard_dates} onChange={load} trigger={<span className="inline-flex items-center gap-1.5 text-mm-primary font-semibold"><CalendarHeart size={15} />Hard dates</span>} />
      </div>
    </section>
  );
};
